
import { Paper, Typography, Box, CircularProgress } from '@material-ui/core';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import { AreaClosed } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import { scaleTime, scaleLinear } from '@visx/scale';
import { formatValue } from '../../utils/formatValue';
import { evaluateThreshold, resolveThresholdColor } from '../../utils/evaluateThreshold';
import type { ThresholdStep, TimeSeriesPoint } from '../../api/types';

interface Props {
  title: string;
  value: number | null;
  unit: string;
  thresholds: ThresholdStep[];
  loading?: boolean;
  sparklineData?: TimeSeriesPoint[];
}

const useStyles = makeStyles({
  root: {
    position: 'relative',
    height: '100%',
    minHeight: 100,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '12px 16px',
    borderRadius: 8,
    overflow: 'hidden',
    transition: 'background-color 0.3s ease',
  },
  title: {
    fontSize: 12,
    fontWeight: 500,
    opacity: 0.85,
    marginBottom: 4,
    textAlign: 'center',
  },
  value: {
    fontSize: 28,
    fontWeight: 700,
    lineHeight: 1.2,
    textAlign: 'center',
  },
  sparkline: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    opacity: 0.25,
    pointerEvents: 'none',
  },
});

export const StatCard = ({
  title,
  value,
  unit,
  thresholds,
  loading,
  sparklineData,
}: Props) => {
  const classes = useStyles();
  const theme = useTheme();

  const resolvedSteps = thresholds.map(s => ({
    ...s,
    color: resolveThresholdColor(s.color),
  }));

  const bgColor = evaluateThreshold(value, resolvedSteps);
  // Use softer background: reduce opacity so text stays readable
  const bgAlpha = theme.palette.type === 'dark' ? '33' : '22';
  const bg = `${bgColor}${bgAlpha}`;
  const textColor = theme.palette.type === 'dark' ? '#F2F1EE' : '#3D3C3C';

  const formatted = formatValue(value, unit);

  const sparkWidth = 200;
  const sparkHeight = 40;

  return (
    <Paper className={classes.root} style={{ backgroundColor: bg }} elevation={0}>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          <Typography className={classes.title} style={{ color: textColor }}>
            {title}
          </Typography>
          <Typography className={classes.value} style={{ color: bgColor }}>
            {formatted}
          </Typography>
          {sparklineData && sparklineData.length > 1 && (
            <Box className={classes.sparkline}>
              <MiniSparkline
                data={sparklineData}
                width={sparkWidth}
                height={sparkHeight}
                color={bgColor}
              />
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

function MiniSparkline({
  data,
  width,
  height,
  color,
}: {
  data: TimeSeriesPoint[];
  width: number;
  height: number;
  color: string;
}) {
  const xScale = scaleTime({
    domain: [
      Math.min(...data.map(d => d.time)),
      Math.max(...data.map(d => d.time)),
    ],
    range: [0, width],
  });
  const yScale = scaleLinear({
    domain: [0, Math.max(...data.map(d => d.value), 1)],
    range: [height, 0],
    nice: true,
  });

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <AreaClosed
        data={data}
        x={d => xScale(d.time)}
        y={d => yScale(d.value)}
        yScale={yScale}
        curve={curveMonotoneX}
        fill={color}
        strokeWidth={0}
      />
    </svg>
  );
}
